/* eslint-disable no-restricted-syntax */

import type { FormKitSchemaDefinition, FormKitSchemaFormKit } from "@formkit/core";
import pluralize from "pluralize";
import type { CrudTableColumn } from "src/router/types";



function getNumberComponent(_schema: CrudTableColumn, fieldName: string): FormKitSchemaFormKit {
  if (fieldName.includes("price")) {
    return {
      $formkit: "primeInputNumber",
      mode: "currency",
      currency: "EUR",
      locale: "nl-NL",
    };
  }

  return {
    $formkit: "primeInputNumber",
    mode: "decimal",
  };
}

function getIntegerComponent(_schema: CrudTableColumn, _fieldName: string): FormKitSchemaFormKit {
  return {
    $formkit: "primeInputNumber",
    mode: "decimal",
    step: 1,
  };
}

function getBooleanComponent(_schema: CrudTableColumn, _fieldName: string): FormKitSchemaFormKit {
  return {
    $formkit: "primeCheckbox",
  };
}

function getDateComponent(_schema: CrudTableColumn, _fieldName: string): FormKitSchemaFormKit {
  return {
    $formkit: "primeDatePicker",
    dateFormat: "dd-mm-yy",
    dateOutputFormat: "yyyy-MM-dd",
  };
}

function getDateTimeComponent(_schema: CrudTableColumn, _fieldName: string): FormKitSchemaFormKit {
  return {
    $formkit: "primeDatePicker",
    dateFormat: "dd-mm-yy HH:mm:ss",
    dateOutputFormat: "yyyy-MM-dd HH:mm:ss",
  };
}

function getParentIdComponent(entityName: string, _schema: CrudTableColumn, _fieldName: string): FormKitSchemaFormKit {
  return {
    $formkit: "selectEntity",
    entity: pluralize(entityName),
    entityLabel: "name",
    entityValue: "id",
    placeholder: `Select ${entityName}...`,
  };
}

/**
 * Resolve a `_id` field name to the entity it references.
 *
 * Uses progressive suffix stripping validated against known entities.
 * For "assignee_user_id" with known entities containing "users":
 *   1. Try "assignee_user" → "assignee-users" → not in set
 *   2. Try "user" → "users" → found ✓
 *
 * Falls back to the full name (minus _id) if no known entity matches.
 */
function resolveEntityFromField(
  fieldName: string,
  knownEntities?: Set<string>,
  fieldMappings?: Record<string, string>,
): { entityName: string; pluralName: string; sourceField: string } {
  const baseName = fieldName.replace(/_id$/, "");

  // Explicit override takes absolute precedence
  if (fieldMappings && fieldMappings[fieldName]) {
    const mapped = fieldMappings[fieldName];
    const mappedKebab = mapped.replace(/_/g, "-");
    return { entityName: mappedKebab, pluralName: pluralize(mappedKebab), sourceField: mapped };
  }

  const segments = baseName.split("_");

  if (knownEntities && knownEntities.size > 0) {
    for (let i = segments.length - 1; i >= 0; i--) {
      const candidate = segments.slice(i).join("_");
      const candidateKebab = candidate.replace(/_/g, "-");
      const candidatePlural = pluralize(candidateKebab);

      if (knownEntities.has(candidatePlural)) {
        return { entityName: candidateKebab, pluralName: candidatePlural, sourceField: candidate };
      }
    }
  }

  // Fallback: use full name (original behavior)
  const kebabName = baseName.replace(/_/g, "-");
  return { entityName: kebabName, pluralName: pluralize(kebabName), sourceField: baseName };
}

function getUuidComponent(
  schema: CrudTableColumn,
  fieldName: string,
  knownEntities?: Set<string>,
  fieldMappings?: Record<string, string>,
): FormKitSchemaFormKit {
  const { entityName, pluralName, sourceField } = resolveEntityFromField(fieldName, knownEntities, fieldMappings);

  return {
    $formkit: "selectEntity",
    entity: pluralName,
    entityValue: "id",
    sourceField,
    placeholder: `Select ${entityName}...`,
  };
}

function getStringComponent(schema: CrudTableColumn, _fieldName: string): FormKitSchemaFormKit {
  if (schema.options) {
    return {
      $formkit: "primeSelect",
      options: schema.options,
    };
  }

  return {
    $formkit: "primeInputText",
  };
}

function isRelation(fieldName: string) {
  const forbiddenWords = ['parent_id'];

  return fieldName.includes("_id") && !forbiddenWords.includes(fieldName);
}

function getJsonEditorComponent(_schema: CrudTableColumn, fieldName: string): FormKitSchemaFormKit {
  return {
    $formkit: "jsonEditor"
  };
}

function getFormKitComponent(entityName: string, schema: CrudTableColumn, fieldName: string, knownEntities?: Set<string>, fieldMappings?: Record<string, string>): FormKitSchemaFormKit {
  if(fieldName === 'parent_id') {
    return getParentIdComponent(entityName, schema, fieldName);
  }

  if (schema.format === "date") {
    return getDateComponent(schema, fieldName);
  }
  if (schema.format === "date-time") {
    return getDateTimeComponent(schema, fieldName);
  }
  if (schema.format === "uuid" || isRelation(fieldName)) {
    return getUuidComponent(schema, fieldName, knownEntities, fieldMappings);
  }
  if (schema.format === "json") {
    return getJsonEditorComponent(schema, fieldName);
  }

  // Then type
  // if (schema.type === "array") {
  //   return getArrayComponent(schema, fieldName);
  // }
  if (schema.type === "number") {
    return getNumberComponent(schema, fieldName);
  }
  if (schema.type === "integer") {
    return getIntegerComponent(schema, fieldName);
  }
  if (schema.type === "boolean") {
    return getBooleanComponent(schema, fieldName);
  }
  if (schema.type === "string") {
    return getStringComponent(schema, fieldName);
  }

  return getStringComponent(schema, fieldName);
}

function formatLabel(fieldName: string): string {
  return fieldName.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function generateFormKitSchema(entityName: string, CrudTableColumn: Record<string, any>, knownEntities?: Set<string>, fieldMappings?: Record<string, string>): FormKitSchemaDefinition {
  return Object.entries(CrudTableColumn).map(([fieldName, fieldSchema]) => ({
    ...getFormKitComponent(entityName, fieldSchema, fieldName, knownEntities, fieldMappings),
    name: fieldName,
    label: formatLabel(fieldName),
  }));
}
